import type { AxiosProgressEvent, CancelTokenSource } from 'axios';
import axios from 'axios';
import { defineStore } from 'pinia';

import type { UploadFileResultDTO } from '@nora-monorepo/file-api-client';
import type {
  ApiResult,
  GetAttachmentsForConversationSortByParameter,
  GetUploadUrl200Response,
  MessageAttachmentDTO,
  SignedUrlDTO,
} from '@nora-monorepo/nora-api-client';
import type { SortOrder } from '@nora-monorepo/shared';
import { useApiStore } from '@nora-monorepo/ui-shared';

export interface Sorting {
  sortBy: GetAttachmentsForConversationSortByParameter;
  sortOrder: SortOrder;
}

export type UploadProgressCallback = (
  progressEvent: AxiosProgressEvent,
) => void;

export const useAttachmentApiStore = defineStore('attachmentApiStore', () => {
  const api = useApiStore();

  const deleteAttachment = async (
    conversationId: string,
    attachmentId: string,
  ): Promise<void> => {
    await api.conversationMessageAttachment.deleteAttachment({
      attachmentId,
      conversationId,
    });
  };

  const editAttachmentFilename = async (
    newName: string,
    attachment: MessageAttachmentDTO,
    conversationId: string,
  ): Promise<ApiResult> => {
    return await api.conversationMessageAttachment.editAttachmentName({
      conversationId,
      attachmentId: attachment.id,
      editAttachmentNamePayload: {
        fileName: newName,
        fileId: attachment.fileId,
      },
    });
  };

  const getUploadUrl = async (conversationId: string): Promise<GetUploadUrl200Response> => {
    const uploadUrlResponse
      = await api.conversationMessageAttachment.getConversationUploadUrl({
        conversationId,
      });

    return uploadUrlResponse.data;
  };

  const uploadFile = async (
    signedUrl: SignedUrlDTO,
    file: File,
    onUploadProgress: UploadProgressCallback,
    cancelTokenSource: CancelTokenSource,
  ): Promise<UploadFileResultDTO> => {
    const formData = new FormData();
    formData.append('file', file);

    const upload = await axios.post<{ data: UploadFileResultDTO }>(
      signedUrl.url,
      formData,
      {
        onUploadProgress,
        cancelToken: cancelTokenSource.token,
      },
    );

    if (!upload.data) {
      throw new Error('Could not upload file');
    }

    return upload.data.data;
  };

  return {
    deleteAttachment,
    editAttachmentFilename,
    getUploadUrl,
    uploadFile,
  };
});
